import React, { useState } from 'react'

import { truncateElementsToMax } from '@/features/audit/utils'
import {
  CommentLogType,
  DocumentLogType,
  EntityAuditLogEntry,
  ReplyLogType,
} from '@/schema'
import { ActionType } from '@/schema/teamwork-user-mapping'
import { SYSTEM_USER_ID } from '@/utils/constants'

import { markdownToPlainTextWithMentions } from '../../utils/slateUtils'

const safeStringify = (val: unknown) =>
  typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val)

const formatStatus = (status: string) => {
  if (!status) return ''
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const OldValueChip: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <span className="line-through text-red-500">{children}</span>

const NewValueChip: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <span className="text-green-600">{children}</span>

const CommentChangeLine: React.FC<{
  entityName: string
  oldValue: string
  newValue: string
}> = ({ entityName, oldValue, newValue }) => {
  const [expanded, setExpanded] = useState(false)

  const oldElements = markdownToPlainTextWithMentions(oldValue)
  const newElements = markdownToPlainTextWithMentions(newValue)

  const oldTruncatedData = truncateElementsToMax(oldElements, 50)
  const newTruncatedData = truncateElementsToMax(newElements, 50)

  const hasLongContent =
    oldTruncatedData.truncated || newTruncatedData.truncated

  const dispOld = expanded ? oldElements : oldTruncatedData.elements
  const dispNew = expanded ? newElements : newTruncatedData.elements

  return (
    <span className="inline break-words break-all w-full">
      <span>
        Edited the {entityName} from <OldValueChip>{dispOld}</OldValueChip> to{' '}
        <NewValueChip>{dispNew}</NewValueChip>
      </span>
      {hasLongContent && (
        <button
          type="button"
          className="inline text-sm text-brand-tertiary-600 font-medium hover:text-brand-tertiary-700 transition-colors border-none bg-transparent p-0 cursor-pointer ml-1"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((v: boolean) => !v)
          }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </span>
  )
}

const CommentInlineText: React.FC<{
  prefix: string
  content: string
}> = ({ prefix, content }) => {
  const [expanded, setExpanded] = useState(false)

  const elements = markdownToPlainTextWithMentions(content)
  const truncatedData = truncateElementsToMax(elements, 50)
  const hasLongContent = truncatedData.truncated
  const disp = expanded ? elements : truncatedData.elements

  return (
    <span className="inline break-words break-all w-full">
      <span>
        {prefix} <span className="text-secondary-700">{disp}</span>
      </span>
      {hasLongContent && (
        <button
          type="button"
          className="inline text-sm text-brand-tertiary-600 font-medium hover:text-brand-tertiary-700 transition-colors border-none bg-transparent p-0 cursor-pointer ml-1"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((v: boolean) => !v)
          }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </span>
  )
}

export interface TflFileAuditMessageProps {
  entry: EntityAuditLogEntry
}

export const TflFileAuditMessage = ({ entry }: TflFileAuditMessageProps) => {
  const type = entry.log_type

  if (type.startsWith('DOCUMENT_')) {
    return renderDocumentAudit(entry, type as DocumentLogType)
  }

  if (type.startsWith('COMMENT_')) {
    return renderCommentAudit(entry, type as CommentLogType)
  }

  if (type.startsWith('REPLY_')) {
    return renderReplyAudit(entry, type as ReplyLogType)
  }

  return null
}

const renderDocumentAudit = (
  entry: EntityAuditLogEntry,
  type: DocumentLogType
) => {
  switch (type) {
    case DocumentLogType.DOCUMENT_CREATED:
      return <span>Uploaded the file</span>

    case DocumentLogType.DOCUMENT_REVISIONED:
      return <span>Uploaded a new version of the file</span>

    case DocumentLogType.DOCUMENT_UPDATED: {
      const field = entry.log_metadata?.field
      if (field === 'status') {
        const oldVal = entry.log_metadata?.old_value as string
        const newVal = entry.log_metadata?.new_value as string

        const oldDisplay = (
          <span className="font-semibold text-primary-900">
            {formatStatus(oldVal)}
          </span>
        )
        const newDisplay = (
          <span className="font-semibold text-primary-900">
            {formatStatus(newVal)}
          </span>
        )

        if (entry.user_id === SYSTEM_USER_ID) {
          return (
            <span>
              Auto changed the status of the file from {oldDisplay} to{' '}
              {newDisplay}
            </span>
          )
        } else {
          return (
            <span>
              Changed status of the file from {oldDisplay} to {newDisplay}
            </span>
          )
        }
      }
      return <span>Updated the file</span>
    }

    case DocumentLogType.DOCUMENT_REJECTED: {
      const version = entry.log_metadata?.extra?.version_number as string
      return (
        <span>
          Raised queries the file on <strong>Version {version || ''}</strong>
        </span>
      )
    }

    case DocumentLogType.DOCUMENT_APPROVED: {
      const version = entry.log_metadata?.extra?.version_number as string
      return (
        <span>
          Approved the file on <strong>Version {version || ''}</strong>
        </span>
      )
    }

    case DocumentLogType.DOCUMENT_DELETED:
      return <span>Deleted the file</span>
    default:
      return null
  }
}

const renderCommentActionableAudit = (entry: EntityAuditLogEntry) => {
  const extra = entry.log_metadata?.extra as Record<string, unknown> | undefined

  const assigneeObj = extra?.assignee as
    | { user_name?: string }
    | string
    | undefined
  const assigneeName =
    typeof assigneeObj === 'string' ? assigneeObj : assigneeObj?.user_name

  const hasDetails = !!(
    extra?.action_type ||
    assigneeName ||
    extra?.priority ||
    extra?.programmer_instructions
  )

  return (
    <span className="flex flex-col gap-1 items-start w-full">
      <span>Marked the comment as Actionable{hasDetails ? ':' : ''}</span>
      {hasDetails && (
        <ul className="list-disc pl-5 m-0 text-secondary-700 w-full break-words break-all">
          {extra?.action_type ? <li>Action Type: {ActionType[extra.action_type as keyof typeof ActionType] || safeStringify(extra.action_type)}</li> : null}
          {assigneeName ? <li>Assignee: {safeStringify(assigneeName)}</li> : null}
          {extra?.priority ? <li>Priority: {safeStringify(extra.priority)}</li> : null}
          {extra?.programmer_instructions ? <li>Programmer Instruction: {safeStringify(extra.programmer_instructions)}</li> : null}
        </ul>
      )}
    </span>
  )
}

const renderCommentAudit = (
  entry: EntityAuditLogEntry,
  type: CommentLogType
) => {
  switch (type) {
    case CommentLogType.COMMENT_CREATED: {
      return (
        <CommentInlineText
          prefix="Added a comment:"
          content={(entry.log_metadata?.extra?.content as string) || ''}
        />
      )
    }

    case CommentLogType.COMMENT_UPDATED: {
      const field = entry.log_metadata?.field
      if (field === 'content') {
        const oldVal = (entry.log_metadata?.old_value as string) || ''
        const newVal = (entry.log_metadata?.new_value as string) || ''
        return (
          <CommentChangeLine
            entityName="comment"
            oldValue={oldVal}
            newValue={newVal}
          />
        )
      }
      if (field === 'status') {
        const isResolved = entry.log_metadata?.new_value === 'resolved'
        return (
          <CommentInlineText
            prefix={
              isResolved ? 'Resolved the comment:' : 'Reopened the comment:'
            }
            content={(entry.log_metadata?.extra?.content as string) || ''}
          />
        )
      }
      return <span>Updated the comment</span>
    }

    case CommentLogType.COMMENT_DELETED:
      return <span>Deleted a comment</span>

    case CommentLogType.COMMENT_ACTIONABLE:
      return renderCommentActionableAudit(entry)

    case CommentLogType.COMMENT_NOT_ACTIONABLE:
      return <span>Marked the comment as Not Actionable</span>

    default:
      return null
  }
}

const renderReplyAudit = (entry: EntityAuditLogEntry, type: ReplyLogType) => {
  switch (type) {
    case ReplyLogType.REPLY_CREATED:
      return (
        <CommentInlineText
          prefix="Added a comment:"
          content={(entry.log_metadata?.extra?.content as string) || ''}
        />
      )

    case ReplyLogType.REPLY_UPDATED: {
      const oldVal = (entry.log_metadata?.old_value as string) || ''
      const newVal = (entry.log_metadata?.new_value as string) || ''
      return (
        <CommentChangeLine
          entityName="comment"
          oldValue={oldVal}
          newValue={newVal}
        />
      )
    }

    case ReplyLogType.REPLY_DELETED:
      return <span>Deleted a comment</span>

    default:
      return null
  }
}
